import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../chrome/Icon';
import { Sheet } from '../shared/Sheet';
import { useSaveQuestionnaire, useGrowth, useCreateGrowth, useMe, useQuestionnaires } from '../../data/queries';
import type { QuestionnaireItem, QuestionnaireQuestionDef, GrowthMetricServer } from '../../data/dto';
import type { Baby } from '../../data/types';
import { QuestionnaireMedia } from './QuestionnaireMedia';
import { useT } from '../../i18n';

interface Props {
  open: boolean;
  baby: Baby;
  item: QuestionnaireItem;
  questions: QuestionnaireQuestionDef[];
  onClose: () => void;
  onOpenMedia?: () => void;
}

/** Unit a measurement question is captured in, given the user's unit system. */
function measurementUnit(code: string, imperial: boolean): string {
  if (code === 'weight') return imperial ? 'lb' : 'kg';
  if (code === 'height') return imperial ? 'in' : 'cm';
  return 'cm'; // head_circumference is always cm, mirroring GrowthAddSheet
}

/** A single labelled field, rendered by the question's input_type. */
function QuestionField({
  q,
  value,
  unit,
  onChange,
}: {
  q: QuestionnaireQuestionDef;
  value: string;
  unit?: string;
  onChange: (v: string) => void;
}) {
  const baseInput =
    'min-w-0 flex-1 border-0 bg-transparent py-3 text-[15px] text-ink outline-none placeholder:text-ink-3/60';
  const fieldWrap = 'flex items-center gap-2.5 rounded-xl border border-line bg-surface px-4 transition-colors focus-within:border-accent';

  if (q.input_type === 'textarea') {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={q.placeholder ?? ''}
        rows={3}
        className="w-full resize-none rounded-xl border border-line bg-surface px-4 py-3 text-[15px] leading-snug text-ink outline-none transition-colors placeholder:text-ink-3/60 focus:border-accent"
      />
    );
  }

  if (q.input_type === 'time') {
    return (
      <div className={fieldWrap}>
        <Icon name="calendar" size={18} color="var(--color-ink-2)" />
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={baseInput}
        />
      </div>
    );
  }

  if (q.input_type === 'measurement') {
    // Stored as "<number> <unit>"; show the number and re-attach the unit.
    const num = value === '' ? '' : (parseFloat(value).toString() === 'NaN' ? '' : parseFloat(value).toString());
    return (
      <div className={fieldWrap}>
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={num}
          onChange={(e) => onChange(e.target.value === '' ? '' : `${e.target.value} ${unit}`)}
          placeholder="—"
          className={baseInput}
        />
        <span className="text-[13px] font-semibold text-ink-2">{unit}</span>
      </div>
    );
  }

  if (q.input_type === 'select_or_text' && q.options) {
    const isCustom = value !== '' && !q.options.includes(value);
    return (
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-wrap gap-1.5">
          {q.options.map((opt) => {
            const on = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(on ? '' : opt)}
                className={[
                  'rounded-3xl px-3 py-2 text-[12.5px] transition-colors',
                  on
                    ? 'bg-ink text-bg font-semibold border-0'
                    : 'border border-line bg-surface text-ink font-medium',
                ].join(' ')}
              >
                {opt}
              </button>
            );
          })}
        </div>
        <div className={fieldWrap}>
          <Icon name="edit" size={16} color="var(--color-ink-2)" />
          <input
            type="text"
            value={isCustom ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={q.placeholder ?? ''}
            className={baseInput}
          />
        </div>
      </div>
    );
  }

  // text (default)
  return (
    <div className={fieldWrap}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={q.placeholder ?? ''}
        className={baseInput}
      />
    </div>
  );
}

export function QuestionnaireSheet({ open, baby, item, questions, onClose, onOpenMedia }: Props) {
  const t = useT();
  const babyId = baby.id ?? '';
  const save = useSaveQuestionnaire();
  const createGrowth = useCreateGrowth();
  const growthQ = useGrowth(babyId || null);
  const questionnairesQ = useQuestionnaires(babyId || null);
  const me = useMe();

  // Read the photos from the live query, not the captured `item` prop — so a
  // freshly uploaded photo appears immediately (the upload invalidates this).
  const livePhotos =
    questionnairesQ.data?.questionnaires.find((q) => q.code === item.code)?.media ?? item.media ?? [];
  const imperial = me.data?.profile?.preferred_units === 'imperial';

  const [answers, setAnswers] = useState<Record<string, string>>(item.answers ?? {});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-seed whenever a different questionnaire is opened.
  useEffect(() => {
    if (open) {
      setAnswers(item.answers ?? {});
      setError(null);
    }
  }, [open, item.code]); // eslint-disable-line react-hooks/exhaustive-deps

  const placeholderForOther = useMemo(
    () => t('questionnaire.ownWords', 'or in your own words…'),
    [t],
  );

  const set = (code: string, v: string) => setAnswers((prev) => ({ ...prev, [code]: v }));

  /**
   * Birth measurements double as growth records: for each measurement entered,
   * create a growth record at the birth date — but only if none exists yet for
   * that metric on that date, so re-editing the questionnaire never duplicates.
   */
  const maybeCreateGrowth = async (current: Record<string, string>) => {
    if (!baby.dobIso) return;
    const measureQs = questions.filter((q) => q.input_type === 'measurement');
    if (measureQs.length === 0) return;
    const existing = growthQ.data ?? [];
    for (const q of measureQs) {
      const raw = current[q.code];
      if (!raw) continue;
      const value = parseFloat(raw);
      if (!Number.isFinite(value) || value <= 0) continue;
      const metric = q.code as GrowthMetricServer;
      const already = existing.some((r) => r.metric === metric && r.recorded_at === baby.dobIso);
      if (already) continue;
      await createGrowth.mutateAsync({
        babyId,
        metric,
        value,
        unit: measurementUnit(q.code, imperial),
        recorded_at: baby.dobIso,
      });
    }
  };

  const submit = async (status: 'draft' | 'completed') => {
    if (saving || !babyId) return;
    setError(null);
    setSaving(true);
    try {
      await save.mutateAsync({ babyId, code: item.code, answers, status });
      await maybeCreateGrowth(answers);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('questionnaire.errorSave', 'Could not save. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const hasAny = Object.values(answers).some((v) => v && v.trim() !== '');

  return (
    <Sheet open={open} onClose={onClose} label={item.title}>
      <div className="px-6 pb-1 shrink-0">
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
          {item.subtitle ?? t('questionnaire.eyebrow', 'A moment to remember')}
        </p>
        <h2 className="mt-1.5 font-serif text-2xl leading-snug tracking-tight text-ink">
          {item.title}
        </h2>
        <p className="text-[12.5px] text-ink-2">
          {t('questionnaire.desc', 'Capture this moment — it will appear on the Journey.')}
        </p>
      </div>

      <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-4.5">
        {questions.map((q) => (
          <div key={q.code}>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-ink-3">
              {q.label}
            </label>
            <QuestionField
              q={q.input_type === 'select_or_text' ? { ...q, placeholder: q.placeholder ?? placeholderForOther } : q}
              value={answers[q.code] ?? ''}
              unit={q.input_type === 'measurement' ? measurementUnit(q.code, imperial) : undefined}
              onChange={(v) => set(q.code, v)}
            />
          </div>
        ))}

        {babyId && (
          <QuestionnaireMedia babyId={babyId} code={item.code} photos={livePhotos} onOpenMedia={onOpenMedia} />
        )}

        {error && (
          <div
            className="rounded-[10px] px-3 py-2.5 text-[12.5px] leading-snug"
            style={{
              background: 'rgba(181, 82, 59, 0.10)',
              border: '1px solid rgba(181, 82, 59, 0.22)',
              color: '#7E3320',
            }}
          >
            {error}
          </div>
        )}
        <div className="h-1" />
      </div>

      <div className="flex gap-2.5 px-5 pt-3.5 pb-7 shrink-0">
        <button
          onClick={() => submit('draft')}
          disabled={saving}
          className="min-h-13 shrink-0 rounded-3xl border border-line bg-transparent px-5 text-[14.5px] font-semibold text-ink disabled:opacity-55"
        >
          {t('questionnaire.saveDraft', 'Save draft')}
        </button>
        <button
          onClick={() => submit('completed')}
          disabled={!hasAny || saving}
          className={[
            'flex min-h-13 flex-1 items-center justify-center gap-2.5 rounded-3xl text-[14.5px] font-semibold transition-all',
            hasAny && !saving ? 'bg-ink text-bg cursor-pointer' : 'text-bg cursor-not-allowed opacity-55',
          ].join(' ')}
          style={
            hasAny && !saving
              ? undefined
              : { background: 'color-mix(in oklch, var(--color-ink) 30%, var(--color-bg))' }
          }
        >
          <Icon name="check" size={16} color="currentColor" />
          {saving ? t('common.saving', 'Saving…') : t('questionnaire.save', 'Save')}
        </button>
      </div>
    </Sheet>
  );
}
